import { IVideo } from '@models/IVideo';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import VideoWithAnimatedTextItem from './VideoWithAnimatedTextItem';

interface Props {
  readonly video: IVideo;
  readonly text: string[];
}

const VideoWithAnimatedText = ({ video, text }: Props) => {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLUListElement>(null);
  const [activeTextIndex, setActiveTextIndex] = useState(0);
  const isPreview = usePreviewMode();

  const { scrollYProgress: videoScrollYProgress } = useScroll({
    target: videoContainerRef,
    offset: ['start end', 'end end'],
  });
  const listContainerScrollYProgress = useTransform(
    videoScrollYProgress,
    [0.4, 1],
    [0, 1]
  );

  return (
    <SoftLoadWrapper>
      <section
        className={classNames(
          'component-padding relative bg-black-100 text-white',
          { 'overflow-hidden': isPreview }
        )}
      >
        <motion.div
          className={classNames('sticky top-0', {
            'h-screen': !isPreview,
            'h-[1080px]': isPreview,
          })}
          ref={videoContainerRef}
        >
          <InViewVideo
            wrapperClasses="h-full"
            videoClasses="[&>div>video]:object-cover h-full overflow-hidden"
            autoPlay
            controls={false}
            video={video}
            inViewThreshold={0}
            hasInVideoPlayButton
            inVideoPlayButtonPositionClassName="top-2.5 left-2.5 md:top-5 md:left-5"
            videoWrapperOpacityProgress={videoScrollYProgress}
          />
        </motion.div>

        <motion.div
          className={classNames(
            'container pointer-events-none relative xl:grid xl:grid-cols-12',
            {
              '-mt-[100vh]': !isPreview,
              '-mt-[1080px] h-[1080px]': isPreview,
            }
          )}
          style={{ opacity: videoScrollYProgress }}
        >
          <motion.ul
            ref={listContainerRef}
            className={classNames(
              'flex flex-col xl:col-start-2 xl:col-end-11',
              {
                'gap-y-[15vh] py-[50vh]': !isPreview,
                'h-full gap-y-[162px] overflow-hidden py-[540px]': isPreview,
              }
            )}
            style={{ opacity: listContainerScrollYProgress }}
          >
            {text.map((value, index) => (
              <VideoWithAnimatedTextItem
                key={value}
                text={value}
                currentItemIndex={index}
                activeItemIndex={activeTextIndex}
                setActiveItemIndex={setActiveTextIndex}
              />
            ))}
          </motion.ul>
        </motion.div>
      </section>
    </SoftLoadWrapper>
  );
};

export default VideoWithAnimatedText;
